import React from 'react'
import { MdOutlineCancel, MdOutlineCheckCircle } from 'react-icons/md'

import { statusFieldView } from '@/Ship/Components/UI/StatusBadge/statusField'

// Приховано разом із кнопкою "Чат" у карточці заявки:
// import { MdOutlineForum } from 'react-icons/md'
// import { openEntityChat } from '@/Containers/CommunicationSection/ChatContainer/openEntityChat'
import { StatusBadge } from '../Components/StatusBadge'
import { REQUEST_STATUS_MAP } from '../Contracts/RequestStatus'
import { RequestViewActions } from '../Hooks/RequestViewActions'
import { RequestFormTemplate } from '../Templates/RequestFormTemplate'

import type { IResourceConfig } from '@/Core/Contracts/UI'

/**
 * Derived once from REQUEST_STATUS_MAP — single source of truth.
 * Antd `type: 'status'` field expects { value, label, color } tuples.
 */
const REQUEST_STATUS_OPTIONS = (
    Object.entries(REQUEST_STATUS_MAP) as Array<[string, { label: string; color: string }]>
).map(([value, info]) => ({ value, label: info.label, color: info.color }))

export const RequestResource: IResourceConfig = {
    title: 'Заявки',
    key: 'Request',
    fields: {
        id: {
            label: 'ID',
            type: 'number',
            isEditable: false,
            /** У фільтрі: оператор (=, >, ≥, <, ≤) або діапазон від–до */
            isFilterRange: true,
        },
        externalCode: {
            label: 'Унікальний код ERP',
            type: 'string',
            inputProps: {
                disabled: true,
            },
        },
        erpNumber: {
            label: 'Номер заявки (ERP)',
            type: 'string',
            isEditable: false,
            inputProps: {
                disabled: true,
            },
        },
        erpCreatedAt: {
            label: 'Дата створення (ERP)',
            type: 'datetime',
            isEditable: false,
            inputProps: {
                disabled: true,
            },
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: REQUEST_STATUS_OPTIONS,
            /** У картці запису статус малює Ship-бейдж із меню зміни */
            view: statusFieldView,
            /** У фільтрі можна обрати кілька статусів */
            isFilterMultiple: true,
            isEditable: false,
            inputProps: {
                disabled: true,
            },
        },
        // Фільтр списку за відповідальним диспетчером (перехід з головної передає його в URL:
        // ?filterDispatcher=/services/users/<uuid>). Диспетчери й старші диспетчери будь-якого
        // статусу, крім видаленого — заявки лишаються й за деактивованими.
        filterDispatcher: {
            label: 'Відповідальний диспетчер',
            type: 'select',
            resource: 'Setting.User',
            filterName: 'responsibleDispatcher',
            isFilterMultiple: true,
            filterOptionsFilter: {
                filterRoles: { op: 'like', value: 'DISPATCHER' },
                status: ['invited', 'active', 'inactive'],
            },
            inputProps: {
                allowClear: true,
                placeholder: 'Відповідальний диспетчер',
            },
        },
        vehicleType: {
            label: 'Тип техніки',
            type: 'select',
            resource: 'Setting.ListVehicleType',
            isEditable: false,
            /** У фільтрі — кілька типів (vehicleType.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: і неактивні типи — заявки з ERP лишаються й за ними */
            filterOptionsFilter: {},
            inputProps: {
                filter: { isActive: true },
                disabled: true,
            },
        },
        organizationUnit: {
            label: 'Підрозділ',
            type: 'select',
            resource: 'Setting.ListOrganizationUnit',
            isEditable: false,
            /** У фільтрі — кілька підрозділів (organizationUnit.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: і неактивні підрозділи — заявки з ERP лишаються й за ними */
            filterOptionsFilter: {},
            inputProps: {
                filter: { isActive: true },
                disabled: true,
            },
        },
        responsibleCustomer: {
            label: 'Відповідальний замовник',
            type: 'select',
            resource: 'Setting.User',
            isEditable: false,
            /** У фільтрі — кілька замовників (responsibleCustomer.uuid in […]) */
            isFilterMultiple: true,
            /**
             * Опції фільтра: замовник з ERP — будь-який користувач (окремої ролі немає),
             * будь-якого статусу, крім видаленого — заявки лишаються й за деактивованими
             */
            filterOptionsFilter: { status: ['invited', 'active', 'inactive'] },
            inputProps: {
                filter: { status: 'active' },
                disabled: true,
            },
        },
        productionObject: {
            label: "Об'єкт виробництва",
            type: 'select',
            resource: 'Setting.ListProductionObject',
            inputProps: {
                filter: { isActive: true },
            },
        },
        constructionObject: {
            label: "Об'єкт будівництва",
            type: 'select',
            resource: 'Setting.ListConstructionObject',
            inputProps: {
                filter: { isActive: true },
            },
        },
        isUrgent: {
            label: 'Терміново',
            type: 'switch',
        },
        comment: {
            label: 'Коментар',
            type: 'textarea',
        },
        cancellationReason: {
            label: 'Причина скасування',
            type: 'select',
            resource: 'Setting.ListCancellationReason',
            inputProps: {
                filter: { isActive: true },
            },
            isEditable: false,
        },
        createdAt: {
            label: 'Дата створення',
            type: 'datetime',
            isEditable: false,
        },
        vehicleNeeds: {
            label: 'Потреби у транспорті',
            type: 'object',
            isEditable: false,
            /** Потреби малює сам шаблон картки (RequestFormTemplate → VehicleNeeds) */
            view: () => null,
        },
    },
    /** Картка без «Службової інформації» ні в перегляді, ні в редагуванні: ID — у шаблоні (DetailShell) */
    hideServiceInfo: true,
    filter: {
        includeFields: [
            'id',
            'status',
            'filterDispatcher',
            'responsibleCustomer',
            'productionObject',
            'constructionObject',
            'vehicleType',
            'organizationUnit',
            'isUrgent',
            'externalCode',
            'erpNumber',
        ],
    },
    table: {
        dataSourceNoTransform: true,
        showColumns: [
            'id',
            'status',
            'erpNumber',
            'responsibleCustomer',
            'vehicleType',
            'organizationUnit',
            'constructionObject',
            'isUrgent',
        ],
        columns: [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 50, render: (v: any) => v ?? '—' },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 160,
                render: (v: any) => <StatusBadge value={v} map={REQUEST_STATUS_MAP} />,
            },
            { title: 'Номер ERP', dataIndex: 'erpNumber', key: 'erpNumber', width: 130, render: (v: any) => v ?? '—' },
            {
                title: 'Відповідальний замовник',
                dataIndex: 'responsibleCustomer',
                key: 'responsibleCustomer',
                width: 180,
                render: (v: any) => v?.shortName || v?.fullName || '—',
            },
            {
                title: 'Тип техніки',
                dataIndex: 'vehicleType',
                key: 'vehicleType',
                width: 130,
                render: (v: any) => v?.title || '—',
            },
            {
                title: 'Підрозділ',
                dataIndex: 'organizationUnit',
                key: 'organizationUnit',
                width: 150,
                render: (v: any) => v?.title || '—',
            },
            {
                title: "Об'єкт будівництва",
                dataIndex: 'constructionObject',
                key: 'constructionObject',
                width: 160,
                render: (v: any) => v?.title || '—',
            },
            {
                title: 'Терміново',
                dataIndex: 'isUrgent',
                key: 'isUrgent',
                width: 80,
                render: (v: any) => (v === true ? 'Так' : v === false ? 'Ні' : '—'),
            },
        ],
        scroll: { x: 'max-content' },
        eventHandlers: {
            onBuildColumns: {
                addVirtualColumns: ({ columns }: any) => {
                    // Місце для додавання віртуальних колонок у майбутньому
                    void columns
                },
                setColumnWidths: ({ column }: any) => {
                    const widths: Record<string, number> = {
                        id: 50,
                        status: 160,
                        erpNumber: 130,
                    }
                    if (widths[column.dataIndex]) {
                        column.width = widths[column.dataIndex]
                    }
                },
                reorderActionsColumn: ({ columns }: any) => {
                    const actionsIndex = columns.findIndex((c: any) => c.key === '_actions')
                    if (actionsIndex > 0) {
                        const [actionsColumn] = columns.splice(actionsIndex, 1)
                        actionsColumn.width = 50
                        columns.unshift(actionsColumn)
                    }
                },
            },
        },
    },
    restUrl: '/services/transport-requests',
    pageUrl: '/requests',
    drawer: {
        width: 1200,
    },
    detailConfig: {
        modeConfig: {
            view: {
                template: RequestFormTemplate,
                onButtons: ({ buttons, crudContext, content }: any) => {
                    const depStore = { formContext: crudContext.formContext, crudContext }

                    // --- Кнопки заявки ---
                    // Кнопку "Чат" приховано на проді (закоментовано за вимогою)
                    // buttons.chat = {
                    //     label: 'Чат',
                    //     icon: React.createElement(MdOutlineForum),
                    //     variant: 'default',
                    //     size: 'middle',
                    //     priority: 'button',
                    //     sort: 1,
                    //     depStore,
                    //     depFn: ({ formContext }: any) => !!formContext?.values?.uuid,
                    //     // Лоадер живе НЕ тут: ButtonGroupStore копіює конфіг
                    //     // spread-ом і get loading() застигає — натомість дровер
                    //     // чату відкривається одразу з оверлей-спінером
                    //     onClick: () => {
                    //         const uuid = crudContext.formContext?.values?.uuid
                    //         if (uuid) {
                    //             // Get-or-create чату заявки і відкриття дровера —
                    //             // весь ланцюг живе у ChatContainer (публічний фасад)
                    //             void openEntityChat('transport_request', String(uuid))
                    //         }
                    //     },
                    // }

                    buttons.complete = {
                        label: 'Завершити',
                        icon: React.createElement(MdOutlineCheckCircle),
                        variant: 'primary',
                        size: 'middle',
                        priority: 'button',
                        sort: 2,
                        depStore,
                        depFn: ({ formContext }: any) => {
                            const status = formContext?.values?.status
                            const permissions = formContext?.values?._permissions || {}
                            return status !== 'completed' && status !== 'cancelled' && !!permissions.complete
                        },
                        onClick: () => {
                            crudContext.eventManager.emit('requestAction:openCompleteConfirm', { crudContext })
                        },
                    }

                    buttons.cancel = {
                        label: 'Скасувати',
                        icon: React.createElement(MdOutlineCancel),
                        variant: 'danger',
                        size: 'middle',
                        priority: 'button',
                        sort: 3,
                        depStore,
                        depFn: ({ formContext }: any) => {
                            const status = formContext?.values?.status
                            const permissions = formContext?.values?._permissions || {}
                            return status !== 'cancelled' && status !== 'completed' && !!permissions.cancel
                        },
                        onClick: () => {
                            crudContext.eventManager.emit('requestAction:openCancelModal', { crudContext })
                        },
                    }

                    content.push(<RequestViewActions key="request-view-actions" crudContext={crudContext} />)
                },
            },
            edit: {
                template: RequestFormTemplate,
            },
            create: {
                template: RequestFormTemplate,
            },
        },
    },
    form: {
        template: RequestFormTemplate,
    },
    settings: {
        searchFields: ['id', 'externalCode', 'erpNumber'],
    },
}
